// SEQ-199: what the widget editor does for a grid widget, with no React in
// it, so the rules are provable under Deno and the settings panel only
// renders them. Same split as widgetEditorState.ts.
//
// Every helper returns a NEW `grid` block (never mutates) and writes explicit
// values, so a saved grid says in plain data what it shows: the cut-off, the
// labels and the bands are all in the config, not implied by a default that a
// later release could change underneath a saved dashboard.

import {
  GRID_MAX_MEASURES,
  GRID_MAX_RULES,
  GRID_MAX_TOP_N,
  GRID_MEASURE_KEYS,
  gridMeasure,
  resolveGridConfig,
  type GridSummaryColumn,
  type PnlGridConfig,
} from "../../lib/pnl/pnlGridConfig";
import type { PnlChartType, PnlWidgetConfig, PnlWidgetLayout } from "../../lib/pnl/pnlWidgetSchema";
import { DEFAULT_PERCENT_BANDS, type BandColour, type FormatBand } from "./transforms/bands";
import { makeWidgetId, type WidgetDraft } from "./widgetEditorState";

/** The whole default grid, written out. Used by the preset and when a widget is switched to the grid type. */
export function explicitGridConfig(grid?: PnlGridConfig): PnlGridConfig {
  const r = resolveGridConfig(grid);
  return {
    measures: r.measures,
    summary: r.summary,
    summaryMeasure: r.summaryMeasure,
    paretoBands: r.paretoBands.map((b) => ({ ...b })),
    paretoRestLabel: r.paretoRestLabel,
    // A typed topN that is not usable (0, 2.5, over the cap) is kept as typed, so the draft keeps failing validation until it is fixed.
    ...(grid?.topN !== undefined ? { topN: grid.topN } : r.topN !== null ? { topN: r.topN } : {}),
    averageRow: r.averageRow,
    freezeFirstColumn: r.freezeFirstColumn,
    formatRules: r.formatRules.map((rule) => ({ measure: rule.measure, bands: rule.bands.map((b) => ({ ...b })) })),
    ...(grid?.sort ? { sort: grid.sort } : {}),
    ...(grid?.hiddenColumns ? { hiddenColumns: grid.hiddenColumns } : {}),
  };
}

export const PROFIT_PARETO_NAME = "Profit Pareto";
const GRID_LAYOUT: PnlWidgetLayout = { w: 4, h: 3 };

/**
 * Josh's Profit Pareto table in one step: a project per row, a profit and
 * profit % pair per month, then Total, % of Total, Cumulative %, Rank and
 * Category with the 80 percent Vital and Trivial cut and his colour bands.
 * It follows the dashboard filter bar's date range like any other widget.
 */
export function profitParetoDraft(existingIds: string[]): WidgetDraft {
  return {
    config: {
      schemaVersion: 1,
      widgetId: makeWidgetId("grid", existingIds),
      name: PROFIT_PARETO_NAME,
      chartType: "grid",
      // Required by the saved-row CHECK (config ? 'metric'). A grid reads measures by rollup field, not by this key.
      metric: "calculated.net_profit",
      groupBy: "project",
      dataGrain: "project",
      aggregation: "sum",
      filters: [],
      dateRange: { preset: "financial_year_to_date" },
      displayAs: "currency",
      layout: GRID_LAYOUT,
      grid: explicitGridConfig(),
    },
    layout: GRID_LAYOUT,
  };
}

/**
 * Applied when the Chart type control switches to or from "grid", the same
 * pattern as monthOnMonthChartTypePatch. Going in: project rows at project
 * grain (the grid's Total column), a full explicit grid block, and none of the
 * fields a grid ignores. Going out: the grid block is dropped. Apply AFTER the
 * month-on-month patch, because that one clears `dataGrain`.
 */
export function gridChartTypePatch(config: PnlWidgetConfig, chartType: PnlChartType): Partial<PnlWidgetConfig> {
  if (chartType === "grid") {
    return {
      groupBy: "project",
      dataGrain: "project",
      secondaryMetric: undefined,
      comparison: undefined,
      sort: undefined,
      limit: undefined,
      metrics: undefined,
      showChange: undefined,
      grid: explicitGridConfig(config.grid),
    };
  }
  if (config.chartType === "grid") return { grid: undefined };
  return {};
}

export function toggleGridMeasure(grid: PnlGridConfig | undefined, key: string): PnlGridConfig {
  const current = explicitGridConfig(grid);
  const measures = current.measures ?? [];
  if (!GRID_MEASURE_KEYS.includes(key)) return current;
  let next: string[];
  if (measures.includes(key)) next = measures.filter((m) => m !== key);
  else next = measures.length >= GRID_MAX_MEASURES ? measures : [...measures, key];
  // Never leave the grid with no measure to show.
  return { ...current, measures: next.length > 0 ? next : measures };
}

export function toggleGridSummary(grid: PnlGridConfig | undefined, key: GridSummaryColumn): PnlGridConfig {
  const current = explicitGridConfig(grid);
  const summary = current.summary ?? [];
  return { ...current, summary: summary.includes(key) ? summary.filter((s) => s !== key) : [...summary, key] };
}

// ── Pareto cut-offs ─────────────────────────────────────────────────────────

export function updateParetoBand(grid: PnlGridConfig | undefined, index: number, change: { upToPercent?: number; label?: string }): PnlGridConfig {
  const current = explicitGridConfig(grid);
  const bands = (current.paretoBands ?? []).map((b, i) => (i === index ? { ...b, ...change } : b));
  return { ...current, paretoBands: bands };
}

export function addParetoBand(grid: PnlGridConfig | undefined): PnlGridConfig {
  const current = explicitGridConfig(grid);
  const bands = current.paretoBands ?? [];
  if (bands.length >= 5) return current;
  const last = bands[bands.length - 1]?.upToPercent ?? 0;
  return { ...current, paretoBands: [...bands, { upToPercent: Math.min(100, last + 10), label: `Band ${bands.length + 1}` }] };
}

export function removeParetoBand(grid: PnlGridConfig | undefined, index: number): PnlGridConfig {
  const current = explicitGridConfig(grid);
  const bands = current.paretoBands ?? [];
  if (bands.length <= 1) return current;
  return { ...current, paretoBands: bands.filter((_, i) => i !== index) };
}

// ── Conditional formatting rules ────────────────────────────────────────────

function withRules(grid: PnlGridConfig | undefined, edit: (rules: NonNullable<PnlGridConfig["formatRules"]>) => NonNullable<PnlGridConfig["formatRules"]>): PnlGridConfig {
  const current = explicitGridConfig(grid);
  return { ...current, formatRules: edit(current.formatRules ?? []) };
}

export function addFormatRule(grid: PnlGridConfig | undefined, measure: string): PnlGridConfig {
  return withRules(grid, (rules) => {
    if (!gridMeasure(measure)?.isPercent || rules.some((r) => r.measure === measure) || rules.length >= GRID_MAX_RULES) return rules;
    return [...rules, { measure, bands: DEFAULT_PERCENT_BANDS.map((b) => ({ ...b })) }];
  });
}

export function removeFormatRule(grid: PnlGridConfig | undefined, measure: string): PnlGridConfig {
  return withRules(grid, (rules) => rules.filter((r) => r.measure !== measure));
}

export function resetFormatRules(grid: PnlGridConfig | undefined): PnlGridConfig {
  const current = explicitGridConfig(grid);
  return { ...current, formatRules: explicitGridConfig().formatRules };
}

export function updateFormatBand(
  grid: PnlGridConfig | undefined,
  measure: string,
  index: number,
  change: Partial<Pick<FormatBand, "min" | "label" | "colour">>,
): PnlGridConfig {
  return withRules(grid, (rules) => rules.map((r) => (r.measure === measure ? { ...r, bands: r.bands.map((b, i) => (i === index ? { ...b, ...change } : b)) } : r)));
}

const NEXT_COLOUR: BandColour[] = ["blue", "grey", "green", "yellow", "orange", "red"];

export function addFormatBand(grid: PnlGridConfig | undefined, measure: string): PnlGridConfig {
  return withRules(grid, (rules) =>
    rules.map((r) => {
      if (r.measure !== measure || r.bands.length >= 8) return r;
      const highest = r.bands.reduce((max, b) => (b.min !== null && b.min > max ? b.min : max), 0);
      const used = new Set<BandColour>(r.bands.map((b) => b.colour));
      const colour = NEXT_COLOUR.find((c) => !used.has(c)) ?? "grey";
      return { ...r, bands: [...r.bands, { min: highest + 10, label: `Band ${r.bands.length + 1}`, colour }] };
    }),
  );
}

export function removeFormatBand(grid: PnlGridConfig | undefined, measure: string, index: number): PnlGridConfig {
  return withRules(grid, (rules) =>
    rules.map((r) => (r.measure === measure && r.bands.length > 1 ? { ...r, bands: r.bands.filter((_, i) => i !== index) } : r)),
  );
}

/** Top N from a text box: blank means every row; anything else is a whole number from 1. */
export function setGridTopN(grid: PnlGridConfig | undefined, raw: string): PnlGridConfig {
  const current = explicitGridConfig(grid);
  const { topN: _dropped, ...rest } = current;
  void _dropped;
  const n = Number(raw);
  return raw.trim() === "" || !Number.isFinite(n) || n < 1 ? rest : { ...rest, topN: Math.min(GRID_MAX_TOP_N, Math.floor(n)) };
}
